import type {
  DocBlock,
  DocListItem,
  DocRun,
  DocText,
} from "@/lib/tool-kit/document";
import { splitItems } from "@/lib/tool-kit/essay-templates/text";

/**
 * Small helpers the lit-essays outline builders share: a labelled outline item that
 * becomes a prompt while its field is empty, and a quotation list.
 */

/** "Claim: …" with the label in bold. */
export function labelledRuns(label: string, value: string): DocRun[] {
  return [{ text: `${label}:`, bold: true }, { text: ` ${value}` }];
}

/** A labelled outline item, or a prompt item while the writer has not filled it in. */
export function itemOrPrompt(
  label: string,
  value: string,
  prompt: DocText
): DocListItem {
  const trimmed = value.trim();
  if (trimmed === "") {
    return { text: prompt, prompt: true };
  }
  return { text: labelledRuns(label, trimmed) };
}

/**
 * A labelled item whose value is a list typed one per line: the label, with each line
 * as a bullet underneath. Empty becomes a prompt.
 */
export function listItemOrPrompt(
  label: string,
  value: string,
  prompt: DocText
): DocListItem {
  const lines = splitItems(value);
  if (lines.length === 0) {
    return { text: prompt, prompt: true };
  }
  return {
    text: [{ text: `${label}:`, bold: true }],
    children: {
      style: "bullet",
      items: lines.map((line) => ({ text: line })),
    },
  };
}

/** One lettered outline as a block. */
export function lettered(items: readonly DocListItem[]): DocBlock {
  return { kind: "list", list: { style: "upper-alpha", items } };
}

/** The writer typed something other than whitespace. */
export function isFilled(value: string): boolean {
  return value.trim() !== "";
}

const NON_WORD = /[^\p{L}\p{N}]+/gu;

/** Lowercased words only, for comparing a thesis with the prompt it may repeat. */
export function normalizedText(text: string): string {
  return text.toLowerCase().replace(NON_WORD, " ").trim();
}

/** Word count of the same normalisation. */
export function wordTotal(text: string): number {
  const words = normalizedText(text);
  return words === "" ? 0 : words.split(" ").length;
}

/**
 * A thesis long enough to make a claim and not a copy of the prompt. The word minimum
 * is a rough proxy for "more than a restatement"; a human reader decides the rest.
 */
export function thesisReady(
  thesis: string,
  prompt: string,
  minWords: number
): boolean {
  const own = normalizedText(thesis);
  return wordTotal(own) >= minWords && own !== normalizedText(prompt);
}

/** At least `minWords` words: the proxy for "more than a phrase or reference". */
export function elaborated(text: string, minWords: number): boolean {
  return wordTotal(text) >= minWords;
}
